import { UseFormReturn } from 'hooks/custom-react-hook-form';
import transform from 'lodash/transform'
import isEqual from 'lodash/isEqual'
import isObject from 'lodash/isObject'
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';



import { Group } from 'lib/api/backend.schemas';



import { filterByAge, filterByCategory, filterByCheckBox, filterByEcOnly, filterByLicence, filterByPrice, filterByProperty, filterByType } from './groupFilters';
import { TFormValues } from './useGroupFilterForm';
import { TGroupPriceConfig, TRequestState } from './usePricesRequestState';


export type TUseFilterAndPricesReturn = {
  maxPrice: Number;
  minPrice: Number;
  filteredList: Group[];
  visibleGroupUIDs: string[];
  currentTypes: any[];
  filteredListWithoutAge: Group[];
  groupsData: Group[];
  initialized: boolean;
  availableCategories: string[];
  currentCategory: string;
  pricesByGroupId?: {[key: string]: number};
  formMethods: UseFormReturn<TFormValues>;
  visibleMinPrice?: number;
  visibleMaxPrice?: number;
  sortedGroups: string[];
  resultAmount: number;
  withPrice: boolean;
  categoryPriceMapping: {[key: string]: number[]};
  setCurrentCategory;
};
const filterArray = (array: Group[], filters: ((groups: Group[]) => Group[])[]) => {
  return filters.reduce((acc, filter) => filter(acc), array);
};
const distinctFilter = (value, index, self) => self.indexOf(value) === index;
export const queryToFormValues = (query, defaultValues) => {
  try {
    return {
      ...defaultValues,
      ...difference(JSON.parse(query), defaultValues),
      price: defaultValues.price,
    };
  } catch {
    return defaultValues;
  }
};

function difference(object, base) {
  function changes(object, base) {
    return transform(object, function (result, value, key) {
      if (!isEqual(value, base?.[key])) {
        result[key] =
          isObject(value) && isObject(base?.[key]) ? changes(value, base?.[key]) : value;
      }
    });
  }
  return changes(object, base) as {};
}
export const objectToQuerystring = (
  {price: _price1 = undefined, ...obj},
  {price: _price2 = undefined, ...defaultValues},
) => {
  const queryStringDifference = difference(obj, defaultValues);
  if (Object.keys(queryStringDifference).length) {
    console.log('different Querystring', queryStringDifference);
    return `filter=${encodeURIComponent(JSON.stringify(queryStringDifference))}`;
  }
  return false;
};

export const useFilterAndPrices = ({
  groupsData,
  pricesRequestState,
  setCurrentCategory,
  formMethods,
  defaultValues,
}: {
  groupsData: Group[];
  category: string;
  pricesRequestState?: TRequestState;
  setCurrentCategory;
  formMethods: UseFormReturn<TFormValues>;
  defaultValues: TFormValues;
}): TUseFilterAndPricesReturn => {
  const {asPath, replace} = useRouter();
  console.log({pricesRequestState});

  const [initialized, setInitialized] = useState(false);

  /** //////////////////////////////////////// */

  const urlSearchParams = new URLSearchParams(window.location.search);

  const queryFilter = urlSearchParams.get('filter');

  const {watch, reset} = formMethods;
  useEffect(() => {
    if (!initialized) {
      if (queryFilter) {
        console.log({filter: queryFilter});
        try {
          reset(queryToFormValues(queryFilter, defaultValues));
          console.log({resetValues: queryToFormValues(queryFilter, defaultValues)});
        } catch {
          // parsing failed, do nothing
        }
        const timeout = setTimeout(() => setInitialized(true), 500);
        return () => clearTimeout(timeout);
      }
    }
  }, []);

  const formValue = watch();
  const currentCategory = formValue.category;

  useEffect(() => {
    const updatedQueryString = objectToQuerystring(formValue, defaultValues);

    const currentParams = new URLSearchParams(window.location.search);

    // Merge the existing query parameters with the updated ones
    if (updatedQueryString) {
      const updatedParams = new URLSearchParams(updatedQueryString);

      // Iterate over existing parameters and add them to the updatedParams if they don't exist already
      currentParams.forEach((value, key) => {
        if (!updatedParams.has(key)) {
          updatedParams.set(key, value);
        }
      });

      if (
        !!updatedQueryString &&
        asPath.split('?')[1] !== updatedParams.toString() &&
        (initialized || !queryFilter)
      ) {
        console.log('updating', formValue);
        replace(asPath.split('?')[0] + '?' + updatedParams.toString(), undefined, {
          shallow: true,
        });
        setInitialized(true);
      }
    } else if (!updatedQueryString && initialized && asPath.split('?')[1]?.length) {
      replace(asPath.split('?')[0], undefined, {
        shallow: true,
      });
    }
  }, [formValue]);
  // console.log(path, objectToQuerystring(formValue), new URLSearchParams(objectToQuerystring(formValue)).get('price'));
  const availableCategories = useMemo(
    () => groupsData.map(group => group.category).filter(distinctFilter),
    [groupsData],
  );
  let pricesAvailableGroups,
    minPrice,
    maxPrice,
    categoryPriceMapping: {[key: string]: number[]} = {};

  useEffect(() => {
    if (initialized) {
      reset({...defaultValues, category: currentCategory});
    }
  }, [currentCategory]);
  if (
    !!pricesRequestState &&
    Object.values(pricesRequestState[currentCategory]).length > 0 &&
    Object.values(pricesRequestState[currentCategory]).every((item: any) => !item.loading)
  ) {
    // const availableGroupIds = filterByType(groupsData, currentTypes).map(item => item.id);
    const availableGroupIds = groupsData.map(item => item.id);
    const pricesForCurrentCatgegory = pricesRequestState[currentCategory];
    const getPricesForCategory = (groups: {[key: string]: TGroupPriceConfig}) => {
      return Object.entries(groups)
        .filter(
          ([key, item]) =>
            availableGroupIds.includes(key) &&
            !item.hidden &&
            typeof item.price === 'string' &&
            !item.loading,
        )
        .map(([_, item]) => parseFloat((item?.price).replace(',', '.')));
    };
    pricesAvailableGroups = getPricesForCategory(pricesForCurrentCatgegory);
    Object.keys(pricesRequestState).forEach(cat => {
      categoryPriceMapping[cat] = getPricesForCategory(pricesRequestState[cat]);
    });
  }
  const pricesByGroupId = !!pricesRequestState
    ? Object.entries(pricesRequestState[currentCategory]).reduce((acc, [key, priceConfig]) => {
        acc[key] = pricesRequestState[currentCategory][key]?.price
          ? parseFloat(priceConfig.price.replace(',', '.'))
          : undefined;
        return acc;
      }, {} as {[key: string]: number})
    : {};

  if (pricesAvailableGroups?.length && pricesAvailableGroups?.length > 0) {
    minPrice = Math.min(...pricesAvailableGroups.filter(price => !isNaN(price)));
    maxPrice = Math.max(...pricesAvailableGroups.filter(price => !isNaN(price)));
  }
  console.log(pricesAvailableGroups);
  const filteredList = filterArray(groupsData, [
    filterByCategory(currentCategory),
    filterByProperty(formValue),
    filterByAge(formValue),
    filterByLicence(formValue),
    filterByCheckBox(formValue),
    filterByEcOnly(formValue),
    filterByPrice({formValue, pricesRequestState, currentCategory}),
  ]);
  const filteredListWithoutAge = filterArray(groupsData, [
    filterByCategory(currentCategory),
    filterByProperty(formValue),
    // filterByAge(formValue),
    filterByLicence(formValue),
    filterByCheckBox(formValue),
    filterByEcOnly(formValue),
    filterByPrice({formValue, pricesRequestState, currentCategory}),
  ]);
  const visibleGroups = filterArray(groupsData, [
    filterByCategory(currentCategory),
    filterByProperty(formValue),
    filterByAge(formValue),
    filterByLicence(formValue),
    filterByCheckBox(formValue),
    filterByEcOnly(formValue),
    filterByType(formValue),
    filterByPrice({formValue, pricesRequestState, currentCategory}),
  ]);
  const sortedGroups = [...groupsData]
  .sort((a, b) => {
    const priceA = pricesByGroupId?.[a.id];
    const priceB = pricesByGroupId?.[b.id];

    // Handle undefined prices: move groups with undefined prices to the bottom
    if (priceA === undefined && priceB !== undefined) return 1;
    if (priceB === undefined && priceA !== undefined) return -1;
    if (priceA === undefined && priceB === undefined) return 0;

    // Sort by price difference multiplied by formValue.sort
    return (priceB - priceA) * formValue.sort;
  })
  .map(group => `${group.id}-${group.type}`);
  const visibleGroupIds = visibleGroups.map(group => group.id);
  const visibleGroupUIDs = visibleGroups.map(group => `${group.id}-${group.type}`);
  const visiblePrices = visibleGroupIds
    .map(groupId => pricesByGroupId[groupId] || undefined)
    .filter(price => !!price);
  const visibleMinPrice = Math.min(...visiblePrices);
  const visibleMaxPrice = Math.max(...visiblePrices);
  console.log({
    visiblePrices,
    maxPrice,
    minPrice,
    visibleMinPrice,
    visibleMaxPrice,
    filteredList,
    visibleGroupIds,
    resultAmount: visibleGroupIds?.length,
    currentTypes: formValue?.type || [],
    filteredListWithoutAge,
    formMethods,
  });
  return {
    withPrice: !!pricesRequestState,
    maxPrice,
    minPrice,
    visibleMinPrice,
    visibleMaxPrice,
    groupsData: [...groupsData],
    currentCategory,
    filteredList,
    categoryPriceMapping,
    availableCategories,
    pricesByGroupId,
    visibleGroupUIDs,
    initialized,
    sortedGroups,
    resultAmount: visibleGroupIds?.length,
    currentTypes: formValue?.type || [],
    filteredListWithoutAge,
    formMethods,
    setCurrentCategory,
  };
};